(() => {
  "use strict";

  if (window.ReinforcedBedsQuizLoaded) return;
  window.ReinforcedBedsQuizLoaded = true;

  const BED_QUESTIONS = [
    {
      id: "bed_use",
      title: "Who is the bed mainly for?",
      help: "This helps us prioritise strength, safety and day-to-day practicality.",
      options: [
        option("Everyday adult use", { domestic: 3 }),
        option("A higher-weight or plus-size user", { heavy: 6 }),
        option("A user who specifically needs bariatric support", { heavy: 7, bariatric: 10 }, { requireBariatric: true }),
        option("A child or teenager", { child: 4, bunk: 1 }),
        option("An older person or someone with limited mobility", { care: 4, adjustable: 3 }),
        option("Contract or supported accommodation", { contract: 6, crib5: 3, durable: 4 })
      ]
    },
    weightQuestion("bed"),
    sizeQuestion(),
    {
      id: "occupancy",
      title: "How many people will regularly use it?",
      help: "For two sleepers, always check both the per-person and total bed weight limits.",
      options: [
        option("One person", { single_user: 2 }),
        option("Two people", { couple: 4, motion_stable: 2 }),
        option("Occasional guest use", { guest: 3, contract: 1 })
      ]
    },
    {
      id: "priority",
      title: "What matters most?",
      help: "Choose the one feature you would least like to compromise on.",
      options: [
        option("Maximum strength and stability", { heavy: 7, durable: 6, mesh: 3 }),
        option("Useful storage", { ottoman: 7, drawers: 5, storage: 6 }),
        option("Adjustable positioning and easier movement", { adjustable: 9, care: 4 }),
        option("A compact or space-saving layout", { bunk: 7, folding: 5, compact: 4 }),
        option("A stylish upholstered finish", { upholstered: 8, divan: 3 })
      ]
    },
    {
      id: "material",
      title: "Which bed style do you prefer?",
      help: "You can choose ‘no preference’ and let the practical answers lead.",
      options: [
        option("No preference", {}),
        option("Metal frame", { metal: 6, mesh: 2 }),
        option("Solid wood", { wood: 6, slats: 2 }),
        option("Upholstered or divan", { upholstered: 5, divan: 5 })
      ]
    },
    {
      id: "storage",
      title: "Do you need under-bed storage?",
      help: "For ottomans, think about where you have room to lift the base.",
      options: [
        option("No storage needed", { no_storage: 2 }),
        option("Drawers", { drawers: 7, storage: 4 }, { requireAny: ["drawers"] }),
        option("Ottoman – open from the foot", { ottoman: 7, end_lift: 6, storage: 5 }, { requireAny: ["end_lift"] }),
        option("Ottoman – open from the side", { ottoman: 7, side_lift: 7, storage: 5 }, { requireAny: ["side_lift"] }),
        option("Maximum storage; lift direction is flexible", { ottoman: 8, storage: 7 }, { requireAny: ["ottoman"] })
      ]
    },
    {
      id: "mobility",
      title: "Would powered adjustment help?",
      help: "An adjustable bed can support comfort, circulation, transfers and independent positioning.",
      options: [
        option("Yes, it is important", { adjustable: 10, motorised: 7, care: 3 }, { requireAny: ["adjustable", "motorised"] }),
        option("It may be useful", { adjustable: 4, motorised: 3 }),
        option("No", { fixed: 3 })
      ]
    },
    {
      id: "setting",
      title: "Where will the bed be used?",
      options: [
        option("Private home", { domestic: 4 }),
        option("Rental, HMO or student accommodation", { contract: 5, durable: 5 }),
        option("Care home or supported living", { care: 6, contract: 5, crib5: 3 }),
        option("Hotel, hostel or activity centre", { contract: 7, durable: 4 }),
        option("A demanding or secure environment", { contract: 7, anti_vandal: 5, durable: 7 })
      ]
    },
    {
      id: "delivery",
      title: "How quickly do you need it?",
      help: "Availability changes, so the product page remains the final source for lead times.",
      options: [
        option("As soon as possible", { in_stock: 6, quick_delivery: 5 }),
        option("Within two weeks", { in_stock: 2, made_to_order: 2 }),
        option("I can wait for the best match", { made_to_order: 3 })
      ]
    }
  ];

  const BED_FINDER_QUESTIONS = [
    {
      id: "bed_type",
      title: "What type of bed are you looking for?",
      help: "Choose the closest match and we will rank suitable beds within that type.",
      options: [
        option("Standard bed frame", { fixed: 5, domestic: 3 }),
        option("Bunk or loft bed", { bunk: 12, compact: 5 }, { requireAny: ["bunk"] }),
        option("Ottoman or storage bed", { ottoman: 10, storage: 8 }, { requireAny: ["ottoman", "storage"] }),
        option("Divan or bed base", { divan: 10, upholstered: 3 }, { requireAny: ["divan"] }),
        option("Adjustable bed", { adjustable: 12, motorised: 8, care: 3 }, { requireAny: ["adjustable", "motorised"] }),
        option("Folding or guest bed", { folding: 12, guest: 6, compact: 4 }, { requireAny: ["folding"] })
      ]
    },
    sizeQuestion(),
    {
      id: "bed_style",
      title: "Which bed style do you prefer?",
      help: "Choose no preference if strength and weight capacity matter more than appearance.",
      options: [
        option("No preference", {}),
        option("Metal", { metal: 9, mesh: 3 }),
        option("Wood", { wood: 9, slats: 3 }),
        option("Upholstered", { upholstered: 9, divan: 4 })
      ]
    },
    {
      id: "slat_type",
      title: "Which mattress support would you prefer?",
      help: "This is the base beneath the mattress. Choose no preference if you are unsure.",
      options: [
        option("No preference", {}),
        option("Solid wooden slats", { slats: 10, wood: 2 }),
        option("Metal mesh platform", { mesh: 10, metal: 2, durable: 3 }),
        option("Solid divan or upholstered base", { divan: 10, upholstered: 4 })
      ]
    },
    {
      id: "occupancy",
      title: "How many people will regularly use the bed?",
      help: "For two sleepers, we will prioritise beds whose stated limits safely cover both people.",
      options: [
        option("One person", { single_user: 3 }),
        option("Two people", { couple: 6, motion_stable: 3 }),
        option("Occasional guest use", { guest: 5, domestic: 1 })
      ]
    },
    weightQuestion("bed")
  ];

  const MATTRESS_QUESTIONS = [
    {
      id: "mattress_use",
      title: "Who is the mattress mainly for?",
      options: [
        option("Everyday adult use", { domestic: 3 }),
        option("A higher-weight or plus-size user", { heavy: 7, durable: 4 }),
        option("A user who specifically needs bariatric support", { heavy: 8, bariatric: 12, durable: 5 }, { requireBariatric: true }),
        option("A child or teenager", { child: 3, bunk: 2 }),
        option("An older person or someone with care needs", { care: 5, pressure_relief: 3 }),
        option("Contract or supported accommodation", { contract: 7, crib5: 4, durable: 5 })
      ]
    },
    weightQuestion("mattress"),
    sizeQuestion(),
    {
      id: "special_use",
      title: "Will it be used in a specialist setup?",
      help: "If the answer narrows the range to only a few suitable products, we will show those matches immediately.",
      options: [
        option("No – standard domestic bed", { domestic: 3 }),
        option("Bunk or high sleeper", { bunk: 12, low_profile: 7 }, { requireAny: ["bunk", "low_profile"], finish: true, resultLimit: 4 }),
        option("Adjustable bed", { adjustable: 12, flexible: 6 }, { requireAny: ["adjustable", "flexible"], finish: true, resultLimit: 4 }),
        option("Care, hotel or other contract setting", { contract: 10, crib5: 7 }, { requireAny: ["contract", "crib5"] }),
        option("Secure or challenging-behaviour setting", { anti_vandal: 12, contract: 8, waterproof: 4 }, { requireAny: ["anti_vandal"], finish: true, resultLimit: 4 })
      ]
    },
    {
      id: "firmness",
      title: "How firm should it feel?",
      help: "Body weight and bed base can change how firmness feels in practice.",
      options: [
        option("Medium or gently cushioned", { medium: 7, pillow_top: 3 }),
        option("Firm and supportive", { firm: 8 }),
        option("Extra firm with very little sink", { extra_firm: 10, brick: 5 }),
        option("Not sure – choose from my other answers", {})
      ]
    },
    {
      id: "sleep_position",
      title: "What is your main sleeping position?",
      options: [
        option("Side", { pressure_relief: 7, contouring: 4, medium: 2 }),
        option("Back", { spinal_support: 7, firm: 3 }),
        option("Front", { firm: 6, extra_firm: 2 }),
        option("A mixture", { responsive: 5, pocket_sprung: 3 })
      ]
    },
    {
      id: "comfort_need",
      title: "Which comfort issue matters most?",
      options: [
        option("Back support and alignment", { spinal_support: 9, firm: 4, extra_firm: 2 }),
        option("Pressure relief for shoulders, hips or joints", { pressure_relief: 9, memory_foam: 4 }),
        option("Preventing dipping and sagging", { durable: 8, extra_firm: 5, reinforced_edges: 4 }),
        option("Reducing movement from a partner", { motion_isolation: 9, pocket_sprung: 6 }),
        option("No specific issue", {})
      ]
    },
    {
      id: "construction",
      title: "Do you prefer a particular construction?",
      options: [
        option("No preference", {}),
        option("Pocket sprung", { pocket_sprung: 8, responsive: 3 }),
        option("Memory foam", { memory_foam: 8, contouring: 5, pressure_relief: 3 }),
        option("Hybrid foam and springs", { hybrid: 9, pocket_sprung: 3, memory_foam: 3 }),
        option("Traditional open coil", { open_coil: 8, durable: 2 })
      ]
    },
    {
      id: "temperature",
      title: "Do you tend to sleep hot?",
      options: [
        option("Yes", { cooling: 8, breathable: 6, vents: 3 }),
        option("Sometimes", { cooling: 3, breathable: 3 }),
        option("No", {})
      ]
    },
    {
      id: "protection",
      title: "Does it need specialist protection?",
      options: [
        option("No", {}),
        option("Waterproof and wipe-clean", { waterproof: 10, hygiene: 6 }, { requireAny: ["waterproof"] }),
        option("Incontinence protection", { waterproof: 10, incontinence: 9, hygiene: 6 }, { requireAny: ["waterproof", "incontinence"] }),
        option("Anti-vandal or tamper-resistant", { anti_vandal: 12, waterproof: 4, contract: 4 }, { requireAny: ["anti_vandal"] })
      ]
    },
    {
      id: "maintenance",
      title: "How much mattress maintenance is practical?",
      options: [
        option("I prefer a no-turn mattress", { no_turn: 7 }),
        option("Rotating it is fine", { rotate: 4 }),
        option("I can turn and rotate it", { double_sided: 7, turnable: 6 }),
        option("No preference", {})
      ]
    },
    {
      id: "delivery",
      title: "How quickly do you need it?",
      help: "Availability changes, so confirm the live lead time on the recommended product page.",
      options: [
        option("As soon as possible", { in_stock: 6, quick_delivery: 5 }),
        option("Within two weeks", { in_stock: 2, made_to_order: 2 }),
        option("I can wait for the best match", { made_to_order: 3 })
      ]
    }
  ];

  const KEYWORD_RULES = [
    rule("metal", ["metal bed", "steel frame", "steel bed", "welded mesh"]),
    rule("mesh", ["mesh base", "welded mesh"]),
    rule("wood", ["wooden bed", "solid wood", "wood frame", "wooden frame", "oak bed"]),
    rule("slats", ["solid slats", "wooden slats", "thick slats"]),
    rule("upholstered", ["upholstered", "fabric bed", "headboard"]),
    rule("divan", ["divan"]),
    rule("ottoman", ["ottoman"]),
    rule("storage", ["storage", "drawer", "ottoman"]),
    rule("drawers", ["drawer", "drawers"]),
    rule("side_lift", ["side lift", "side-lift"]),
    rule("end_lift", ["end lift", "end-lift", "foot end"]),
    rule("adjustable", ["adjustable", "profiling bed", "motorised", "motorized"]),
    rule("motorised", ["motorised", "motorized", "quiet motor", "electric bed"]),
    rule("bunk", ["bunk", "high sleeper", "mid sleeper"]),
    rule("folding", ["folding bed", "foldaway"]),
    rule("compact", ["compact", "space saving", "space-saving"]),
    rule("contract", ["contract", "hotel", "hostel", "student accommodation", "care home", "hmo"]),
    rule("care", ["care home", "assisted living", "elderly", "healthcare", "hospital"]),
    rule("crib5", ["crib 5", "crib5", "bs7177", "source 5"]),
    rule("anti_vandal", ["anti-vandal", "anti vandal", "tamper", "challenging behaviour", "secure environment"]),
    rule("heavy", ["heavy duty", "heavy-duty", "reinforced", "higher weight", "plus size", "plus-size"]),
    rule("bariatric", ["bariatric"]),
    rule("durable", ["durable", "durability", "long-lasting", "high-use", "demanding environment", "reinforced"]),
    rule("fixed", ["fixed frame", "non-adjustable"]),
    rule("in_stock", ["in stock", "next day", "ready to dispatch"]),
    rule("quick_delivery", ["next day", "1-2 working days", "3-5 working days"]),
    rule("made_to_order", ["made to order", "production lead time"]),
    rule("pocket_sprung", ["pocket sprung", "pocket spring", "pocketed spring", "pocket springs"]),
    rule("memory_foam", ["memory foam", "visco foam"]),
    rule("open_coil", ["open coil", "open-coil"]),
    rule("hybrid", ["hybrid"]),
    rule("medium", ["medium feel", "medium tension", "medium firmness", "medium mattress"]),
    rule("firm", ["firm feel", "firm tension", "firm support", "firm mattress"]),
    rule("extra_firm", ["extra firm", "extra-firm", "ultra firm", "very firm"]),
    rule("brick", ["brick mattress", "the brick"]),
    rule("pillow_top", ["pillow top", "pillow-top"]),
    rule("pressure_relief", ["pressure relief", "pressure-relieving", "pressure points"]),
    rule("spinal_support", ["spinal", "back support", "orthopaedic", "orthopedic", "alignment"]),
    rule("contouring", ["contour", "moulds around", "molds around"]),
    rule("responsive", ["responsive", "independent springs"]),
    rule("motion_isolation", ["motion isolation", "partner disturbance", "movement transfer"]),
    rule("cooling", ["cooling", "temperature regulation", "cool gel"]),
    rule("breathable", ["breathable", "airflow"]),
    rule("vents", ["vents", "air vents", "nickel vents"]),
    rule("waterproof", ["waterproof", "wipe-clean", "wipe clean", "fluid resistant"]),
    rule("incontinence", ["incontinence"]),
    rule("hygiene", ["hygienic", "hygiene", "antibacterial", "anti-bacterial"]),
    rule("low_profile", ["low profile", "low-profile", "slim mattress", "bunk mattress"]),
    rule("flexible", ["flexible foam", "suitable for adjustable", "adjustable base"]),
    rule("reinforced_edges", ["reinforced edge", "reinforced border", "side support", "edge support"]),
    rule("no_turn", ["no turn", "no-turn", "single sided", "single-sided"]),
    rule("double_sided", ["double sided", "double-sided", "two sided", "two-sided"]),
    rule("turnable", ["turn regularly", "turn and rotate", "turning handles"]),
    rule("rotate", ["rotate", "rotation handles"])
  ];

  const SIGNAL_LABELS = {
    adjustable: "powered adjustment",
    anti_vandal: "tamper-resistant construction",
    bariatric: "bariatric support",
    breathable: "breathability",
    bunk: "bunk compatibility",
    care: "care needs",
    contract: "contract use",
    cooling: "cooler sleep",
    crib5: "contract fire safety",
    divan: "divan styling",
    drawers: "drawer storage",
    durable: "long-term durability",
    extra_firm: "extra-firm support",
    firm: "firm support",
    heavy: "reinforced strength",
    hybrid: "hybrid construction",
    hygiene: "easy hygiene",
    in_stock: "faster availability",
    memory_foam: "memory-foam comfort",
    metal: "metal construction",
    motion_isolation: "reduced partner disturbance",
    no_turn: "low maintenance",
    open_coil: "open-coil construction",
    ottoman: "ottoman storage",
    pocket_sprung: "pocket-sprung support",
    pressure_relief: "pressure relief",
    side_lift: "side-lift access",
    spinal_support: "back and spinal support",
    storage: "storage",
    upholstered: "upholstered styling",
    waterproof: "waterproof protection",
    wood: "solid-wood construction"
  };

  function option(label, signals, extra = {}) {
    return { label, signals, ...extra };
  }

  function rule(signal, phrases) {
    return { signal, phrases };
  }

  function weightQuestion(prefix) {
    return {
      id: `${prefix}_weight`,
      title: "What is the heaviest sleeper’s approximate weight?",
      help: "We use the heaviest individual—not the combined weight—to screen products. Always verify the product’s stated limit before ordering.",
      options: [
        option("Up to 15 stone / 95 kg", { standard_weight: 3 }, { requiredKg: 95 }),
        option("15–20 stone / 95–127 kg", { heavy: 3 }, { requiredKg: 127 }),
        option("20–30 stone / 127–190 kg", { heavy: 7, bariatric: 2 }, { requiredKg: 190 }),
        option("30–40 stone / 190–254 kg", { heavy: 8, bariatric: 7 }, { requiredKg: 254 }),
        option("Over 40 stone / 254 kg", { bariatric: 12, heavy: 8 }, { requiredKg: 300 })
      ]
    };
  }

  function sizeQuestion() {
    return {
      id: "size",
      title: "Which size do you need?",
      help: "Only products with a matching live variant will be prioritised.",
      options: [
        option("Small single", {}, { size: "small single" }),
        option("Single", {}, { size: "single" }),
        option("Small double", {}, { size: "small double" }),
        option("Double", {}, { size: "double" }),
        option("King", {}, { size: "king" }),
        option("Super king", {}, { size: "super king" })
      ]
    };
  }

  function normalise(value) {
    return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  }

  function inferSignals(product) {
    const text = normalise([
      product.title,
      product.type,
      product.vendor,
      product.description,
      ...(product.tags || []),
      ...(product.variants || []).map((variant) => variant.title)
    ].join(" "));
    const signals = new Set();
    KEYWORD_RULES.forEach(({ signal, phrases }) => {
      if (phrases.some((phrase) => text.includes(normalise(phrase)))) signals.add(signal);
    });
    return { text, signals, maxKg: extractWeightLimit(text) };
  }

  function hasExplicitBariatricClaim(product) {
    const identityText = normalise([
      product.title,
      product.type,
      ...(product.tags || [])
    ].join(" "));
    return identityText.includes("bariatric");
  }

  function extractWeightLimit(text) {
    const patterns = [
      /(?:supports?|supporting|weight limit|weight support|up to)\s*(?:of\s*)?(\d{2,3})\s*(kg|kilograms?|stone|st\b)/gi,
      /(\d{2,3})\s*(kg|kilograms?|stone|st\b)\s*(?:per user|per person|weight limit|support)/gi
    ];
    const values = [];
    patterns.forEach((pattern) => {
      for (const match of text.matchAll(pattern)) {
        const amount = Number(match[1]);
        values.push(match[2].startsWith("kg") || match[2].startsWith("kilogram") ? amount : amount * 6.35029);
      }
    });
    return values.length ? Math.max(...values) : null;
  }

  function availableSizes(product) {
    return (product.variants || [])
      .filter((variant) => variant.available)
      .flatMap((variant) => [variant.title, ...(variant.options || [])])
      .map(normalise);
  }

  function scoreProduct(product, selectedOptions) {
    const inferred = inferSignals(product);
    const desired = new Map();
    selectedOptions.forEach((choice) => {
      Object.entries(choice.signals || {}).forEach(([signal, weight]) => {
        desired.set(signal, (desired.get(signal) || 0) + weight);
      });
    });

    let score = product.available ? 3 : -30;
    let eligible = Boolean(product.available);
    const rejectedBy = [];
    const matches = [];
    desired.forEach((weight, signal) => {
      if (inferred.signals.has(signal)) {
        score += weight;
        matches.push({ signal, weight });
      }
    });

    const sizeChoice = selectedOptions.find((choice) => choice.size);
    if (sizeChoice) {
      const exactSize = normalise(sizeChoice.size);
      const sizes = availableSizes(product);
      const hasSize = sizes.some((size) => size === exactSize || size.startsWith(`${exactSize} `));
      if (hasSize) score += 18;
      else {
        eligible = false;
        rejectedBy.push("size");
      }
    }

    selectedOptions.forEach((choice) => {
      if (!choice.requireAny || !choice.requireAny.length) return;
      const satisfiesRequirement = choice.requireAny.some((signal) => inferred.signals.has(signal));
      if (!satisfiesRequirement) {
        eligible = false;
        rejectedBy.push(choice.requireAny.join(" or "));
      }
    });

    if (selectedOptions.some((choice) => choice.requireBariatric)) {
      const explicitlyBariatric = hasExplicitBariatricClaim(product);
      const verifiedForThirtyStone = inferred.maxKg !== null && inferred.maxKg >= 190;
      if (!explicitlyBariatric && !verifiedForThirtyStone) {
        eligible = false;
        rejectedBy.push("bariatric support not confirmed");
      }
    }

    const weightChoice = selectedOptions.find((choice) => choice.requiredKg);
    if (weightChoice && inferred.maxKg) {
      if (inferred.maxKg + 1 < weightChoice.requiredKg) {
        eligible = false;
        rejectedBy.push("known weight limit");
      }
      else {
        score += 18;
        matches.push({ signal: "weight_verified", weight: 18 });
      }
    } else if (weightChoice) {
      const explicitlyBariatric = hasExplicitBariatricClaim(product);
      const reinforcedForHigherWeight = inferred.signals.has("heavy") || explicitlyBariatric;
      if (weightChoice.requiredKg <= 127 && reinforcedForHigherWeight) {
        score += 7;
        matches.push({ signal: "heavy", weight: 7 });
      } else if (weightChoice.requiredKg <= 190 && explicitlyBariatric) {
        score += 10;
        matches.push({ signal: "bariatric", weight: 10 });
      } else if (weightChoice.requiredKg >= 127) {
        eligible = false;
        rejectedBy.push("per-person weight limit not confirmed");
      }
    }

    return {
      product,
      score,
      eligible,
      rejectedBy,
      maxKg: inferred.maxKg,
      matches: matches.sort((a, b) => b.weight - a.weight)
    };
  }

  function bestVariantUrl(product, selectedOptions) {
    const sizeChoice = selectedOptions.find((choice) => choice.size);
    if (!sizeChoice) return product.url;
    const exactSize = normalise(sizeChoice.size);
    const variant = (product.variants || []).find((item) =>
      item.available && [item.title, ...(item.options || [])].some((value) => {
        const size = normalise(value);
        return size === exactSize || size.startsWith(`${exactSize} `);
      })
    );
    return variant ? variant.url : product.url;
  }

  function escapeHtml(value) {
    return String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function normaliseMoney(value) {
    return String(value || "")
      .replace(/&pound;|&#163;/gi, "£")
      .replace(/&amp;/gi, "&");
  }

  class ReinforcedBedsQuiz {
    constructor(root) {
      this.root = root;
      this.type = root.dataset.quizType;
      this.questions = this.type === "bed" ? BED_FINDER_QUESTIONS : MATTRESS_QUESTIONS;
      this.resultsCount = Number(root.dataset.resultsCount || 3);
      this.stage = root.querySelector("[data-rbq-stage]");
      this.progress = root.querySelector("[data-rbq-progress]");
      this.products = this.readProducts();
      this.answers = [];
      this.index = 0;
      this.resultLimitOverride = null;
      this.renderQuestion();
    }

    readProducts() {
      try {
        const parsed = JSON.parse(this.root.querySelector("[data-rbq-products]").textContent);
        const products = Array.isArray(parsed)
          ? parsed
          : Object.values(parsed.collections || {}).flat();
        return Array.from(new Map(products.map((product) => [product.handle, product])).values());
      } catch (error) {
        console.warn("Reinforced Beds quiz could not read product data.", error);
        return [];
      }
    }

    renderQuestion() {
      const question = this.questions[this.index];
      const percentage = Math.round((this.index / this.questions.length) * 100);
      this.progress.style.width = `${percentage}%`;
      const existing = this.answers[this.index];
      this.stage.innerHTML = `
        <div class="rbq__question" data-rbq-question>
          <p class="rbq__step">Question ${this.index + 1} of ${this.questions.length}</p>
          <h3>${escapeHtml(question.title)}</h3>
          ${question.help ? `<p class="rbq__help">${escapeHtml(question.help)}</p>` : ""}
          <div class="rbq__options" role="radiogroup" aria-label="${escapeHtml(question.title)}">
            ${question.options.map((choice, choiceIndex) => `
              <button
                class="rbq__option${existing === choiceIndex ? " is-selected" : ""}"
                type="button"
                role="radio"
                aria-checked="${existing === choiceIndex}"
                data-choice="${choiceIndex}"
              >
                <span class="rbq__radio" aria-hidden="true"></span>
                <span>${escapeHtml(choice.label)}</span>
              </button>
            `).join("")}
          </div>
          <div class="rbq__nav">
            <button class="rbq__back" type="button" data-back ${this.index === 0 ? "disabled" : ""}>Back</button>
          </div>
        </div>`;

      this.stage.querySelectorAll("[data-choice]").forEach((button) => {
        button.addEventListener("click", () => this.choose(Number(button.dataset.choice)));
      });
      this.stage.querySelector("[data-back]").addEventListener("click", () => this.back());
    }

    choose(choiceIndex) {
      this.answers[this.index] = choiceIndex;
      const choice = this.questions[this.index].options[choiceIndex];
      if (choice.finish) {
        this.resultLimitOverride = choice.resultLimit || this.resultsCount;
        this.renderResults();
      } else if (this.index < this.questions.length - 1) {
        this.index += 1;
        this.renderQuestion();
      } else {
        this.renderResults();
      }
    }

    back() {
      if (this.index === 0) return;
      this.index -= 1;
      this.renderQuestion();
    }

    selectedOptions() {
      return this.answers.map((answerIndex, questionIndex) => this.questions[questionIndex].options[answerIndex]);
    }

    renderResults() {
      this.progress.style.width = "100%";
      const selected = this.selectedOptions();
      const ranked = this.products
        .map((product) => scoreProduct(product, selected))
        .filter((result) => result.eligible)
        .sort((a, b) => b.score - a.score)
        .slice(0, this.resultLimitOverride || this.resultsCount);

      if (!ranked.length) {
        const productLabel = this.type === "bed" ? "beds" : "mattresses";
        const unavailableMessage = this.products.length
          ? `None of the ${productLabel} has enough confirmed suitability or per-person weight-limit information for these answers. Please contact the Reinforced Beds team for a checked recommendation.`
          : "No products are available in the collection connected to this quiz. Please contact the Reinforced Beds team for a tailored recommendation.";
        this.stage.innerHTML = `
          <div class="rbq__results">
            <h3>We need a little human help</h3>
            <p>${unavailableMessage}</p>
            <button class="rbq__restart" type="button" data-restart>Start again</button>
          </div>`;
        this.stage.querySelector("[data-restart]").addEventListener("click", () => this.restart());
        return;
      }

      this.stage.innerHTML = `
        <div class="rbq__results">
          <p class="rbq__eyebrow">Your strongest matches</p>
          <h3>Recommended for your needs</h3>
          <p class="rbq__result-intro">We compared your answers with the live product titles, tags, variants and descriptions in this collection.</p>
          <div class="rbq__cards">
            ${ranked.map((result, index) => this.productCard(result, index, selected)).join("")}
          </div>
          <div class="rbq__notice">
            <strong>Important:</strong> Recommendations are guidance, not medical advice. Confirm the exact per-person and total weight limits, dimensions, availability and suitability on the product page before ordering.
          </div>
          <button class="rbq__restart" type="button" data-restart>Retake the quiz</button>
        </div>`;
      this.stage.querySelector("[data-restart]").addEventListener("click", () => this.restart());
    }

    productCard(result, index, selected) {
      const { product, matches, maxKg } = result;
      const reasons = matches
        .filter((match) => match.signal !== "weight_verified")
        .map((match) => SIGNAL_LABELS[match.signal])
        .filter(Boolean)
        .filter((reason, reasonIndex, all) => all.indexOf(reason) === reasonIndex)
        .slice(0, 3);
      if (maxKg && matches.some((match) => match.signal === "weight_verified")) {
        reasons.unshift(`a stated support figure around ${Math.round(maxKg)} kg`);
      }
      const reasonText = reasons.length ? `Strong match for ${reasons.join(", ")}.` : "The closest overall match from the connected collection.";
      return `
        <article class="rbq__card">
          <div class="rbq__rank">${index === 0 ? "Best match" : `Alternative ${index}`}</div>
          ${product.image ? `<img src="${escapeHtml(product.image)}" alt="" loading="lazy" width="720" height="540">` : ""}
          <div class="rbq__card-body">
            <h4>${escapeHtml(product.title)}</h4>
            <p class="rbq__price">From ${escapeHtml(normaliseMoney(product.price))}</p>
            <p>${escapeHtml(reasonText)}</p>
            <a class="rbq__cta" href="${escapeHtml(bestVariantUrl(product, selected))}">View this product</a>
          </div>
        </article>`;
    }

    restart() {
      this.answers = [];
      this.index = 0;
      this.resultLimitOverride = null;
      this.renderQuestion();
      this.root.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function initialise(root) {
    if (!root.dataset.rbqReady && !root.querySelector("[data-rbq-app]")?.hidden) {
      root.dataset.rbqReady = "true";
      new ReinforcedBedsQuiz(root);
    }
  }

  function injectCollectionQuizCard() {
    const handleMatch = window.location.pathname.toLowerCase().match(/^\/collections\/([^/?#]+)/);
    if (!handleMatch) return;

    const collectionHandle = handleMatch[1];
    const isMattressCollection = /(^|-)mattress(es)?(-|$)/.test(collectionHandle);
    const isBedCollection = !isMattressCollection && /(^|-)(bed|beds)(-|$)/.test(collectionHandle);
    if (!isMattressCollection && !isBedCollection) return;

    const grid = document.querySelector("#product-grid");
    if (!grid || grid.querySelector("[data-rbq-promo-card]")) return;

    const cardContent = isMattressCollection
      ? {
          href: "/pages/mattress-quiz",
          ariaLabel: "Open the mattress finder quiz",
          heading: "Not sure which mattress is right for you?",
          copy: "Answer a few detailed questions and get recommendations matched to your support, comfort and size needs.",
          button: "Start mattress finder"
        }
      : {
          href: "/pages/mattress-quiz?view=bed-quiz",
          ariaLabel: "Open the bed finder quiz",
          heading: "Not sure which bed is right for you?",
          copy: "Answer a few detailed questions and get recommendations matched to your strength, style, storage and size needs.",
          button: "Start bed finder"
        };

    if (!document.getElementById("rbq-promo-card-styles")) {
      const styles = document.createElement("style");
      styles.id = "rbq-promo-card-styles";
      styles.textContent = `
        #product-grid .rbq-promo-card { display: flex; }
        #product-grid .rbq-promo-card__link { background: #f6f6f4; border: 1px solid #d6d6d6; color: #222321; display: flex; flex: 1; flex-direction: column; font-family: Manrope, sans-serif; min-height: 100%; text-decoration: none; transition: border-color 150ms ease, transform 150ms ease; }
        #product-grid .rbq-promo-card__link:hover, #product-grid .rbq-promo-card__link:focus-visible { border-color: #222321; outline: none; transform: translateY(-2px); }
        #product-grid .rbq-promo-card__visual { align-items: flex-end; background: #222321; color: #fff; display: flex; min-height: 201px; padding: 22px; position: relative; }
        #product-grid .rbq-promo-card__visual::before { border: 1px solid rgba(255,255,255,.34); border-radius: 50%; content: "?"; display: grid; font-size: 2rem; font-weight: 700; height: 58px; place-items: center; position: absolute; right: 20px; top: 20px; width: 58px; }
        #product-grid .rbq-promo-card__label { font-size: .78rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
        #product-grid .rbq-promo-card__body { display: flex; flex: 1; flex-direction: column; padding: 16px 20px 20px; }
        #product-grid .rbq-promo-card__body h3 { color: #222321; font-size: 21px; font-weight: 700; line-height: 1.5; margin: 0 0 14px; }
        #product-grid .rbq-promo-card__body p { color: #555753; font-size: .95rem; line-height: 1.55; margin: 0 0 18px; }
        #product-grid .rbq-promo-card__button { align-self: flex-start; background: #222321; border-radius: 5px; color: #fff; font-size: .9rem; font-weight: 700; margin-top: auto; padding: 14px 18px; text-transform: uppercase; }
      `;
      document.head.appendChild(styles);
    }

    const card = document.createElement("div");
    card.className = "collection__grid__item rbq-promo-card";
    card.setAttribute("data-rbq-promo-card", "true");
    card.innerHTML = `
      <a class="rbq-promo-card__link" href="${cardContent.href}" aria-label="${cardContent.ariaLabel}">
        <div class="rbq-promo-card__visual">
          <span class="rbq-promo-card__label">Reinforced Beds product finder</span>
        </div>
        <div class="rbq-promo-card__body">
          <h3>${cardContent.heading}</h3>
          <p>${cardContent.copy}</p>
          <span class="rbq-promo-card__button">${cardContent.button}</span>
        </div>
      </a>`;

    const products = Array.from(grid.children).filter((item) => !item.hasAttribute("data-rbq-promo-card"));
    grid.insertBefore(card, products[4] || null);
  }

  if (typeof document === "undefined") {
    if (typeof module !== "undefined") {
      module.exports = { MATTRESS_QUESTIONS, bestVariantUrl, extractWeightLimit, inferSignals, scoreProduct };
    }
    return;
  }

  document.querySelectorAll("[data-rbq-root]").forEach(initialise);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", injectCollectionQuizCard, { once: true });
  } else {
    injectCollectionQuizCard();
  }

  const collectionObserver = new MutationObserver(() => injectCollectionQuizCard());
  collectionObserver.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener("shopify:section:load", (event) => {
    event.target.querySelectorAll("[data-rbq-root]").forEach(initialise);
    injectCollectionQuizCard();
  });
})();
